"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { io, Socket } from "socket.io-client";

interface NotificationContextType {
  unreadCount: number;
  chatNotifications: Map<string, number>;
  markChatAsRead: (chatId: string) => void;
  /** Sincroniza contadores con la BD (no depende del servidor de sockets). */
  resyncUnreadNotifications: () => void;
  playNotificationSound: boolean;
  toggleNotificationSound: () => void;
  connectionStatus: "connecting" | "connected" | "disconnected" | "error";
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{
  children: React.ReactNode;
  currentUser?: { id: string } | null;
}> = ({ children, currentUser }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatNotifications, setChatNotifications] = useState<
    Map<string, number>
  >(new Map());
  const [playNotificationSound, setPlayNotificationSound] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<
    "connecting" | "connected" | "disconnected" | "error"
  >("disconnected");
  const socketRef = useRef<Socket>();
  const initialLoadDoneRef = useRef(false);
  const receiveDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const refreshUnreadFromDatabase = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const res = await fetch("/api/chat/unread-summary");
      if (!res.ok) return;
      const data = (await res.json()) as {
        totalCount: number;
        byChat: Record<string, number>;
      };
      const map = new Map<string, number>(Object.entries(data.byChat));
      setChatNotifications(map);
      setUnreadCount(data.totalCount);
      initialLoadDoneRef.current = true;
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("refreshUnreadFromDatabase", e);
      }
    }
  }, [currentUser?.id]);

  const refreshRef = useRef(refreshUnreadFromDatabase);
  refreshRef.current = refreshUnreadFromDatabase;

  useEffect(() => {
    if (!currentUser?.id) {
      setUnreadCount(0);
      setChatNotifications(new Map());
      setConnectionStatus("disconnected");
      return;
    }

    void refreshUnreadFromDatabase();
  }, [currentUser?.id, refreshUnreadFromDatabase]);

  useEffect(() => {
    if (!currentUser?.id) {
      return;
    }

    setConnectionStatus("connecting");

    const socketUrl =
      process.env.NEXT_PUBLIC_SOCKET_URL || "http://localhost:5000";

    if (!socketUrl) {
      console.warn("Socket URL no configurada");
      setConnectionStatus("error");
      return;
    }

    const socket = io(socketUrl, {
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ["polling", "websocket"],
      upgrade: true,
      forceNew: false,
      path: "/socket.io/",
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setConnectionStatus("connected");
      socket.emit("register", currentUser.id);
    });

    socket.on("registerConfirmed", () => {
      if (socket.connected) {
        socket.emit("getUnreadNotifications", currentUser.id);
      }
    });

    socket.on("connect_error", (error: Error) => {
      if (process.env.NODE_ENV === "development") {
        console.warn("Error de conexión socket:", {
          message: error.message,
          url: socketUrl,
        });
      }
    });

    socket.on("disconnect", () => {
      setConnectionStatus("disconnected");
    });

    socket.on("error", (error) => {
      console.error("Error del servidor:", error);
    });

    // El servidor puede llevar contadores desfasados respecto a la BD (p. ej. mensajes desde la app).
    // Ignoramos el payload y leemos siempre desde Prisma.
    socket.on("unreadNotifications", () => {
      void refreshRef.current();
    });

    return () => {
      socket.disconnect();
      initialLoadDoneRef.current = false;
      setConnectionStatus("disconnected");
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!socketRef.current || !currentUser?.id) return;

    const socket = socketRef.current;

    const handleReceiveMessage = (messageData: {
      receiverId?: string;
      senderId?: string;
      chatId: string;
    }) => {
      if (
        messageData.receiverId === currentUser.id &&
        messageData.senderId !== currentUser.id
      ) {
        setUnreadCount((prev) => prev + 1);

        setChatNotifications((prev) => {
          const newMap = new Map(prev);
          const currentCount = newMap.get(messageData.chatId) || 0;
          newMap.set(messageData.chatId, currentCount + 1);
          return newMap;
        });

        if (receiveDebounceRef.current) {
          clearTimeout(receiveDebounceRef.current);
        }
        receiveDebounceRef.current = setTimeout(() => {
          void refreshRef.current();
        }, 350);
      }
    };

    const handleChatRead = ({ chatId }: { chatId: string }) => {
      setChatNotifications((prev) => {
        const newMap = new Map(prev);
        const count = newMap.get(chatId) || 0;

        if (count > 0) {
          newMap.delete(chatId);

          setUnreadCount((prevCount) => Math.max(0, prevCount - count));
        }

        return newMap;
      });
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("chatRead", handleChatRead);

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("chatRead", handleChatRead);
      if (receiveDebounceRef.current) {
        clearTimeout(receiveDebounceRef.current);
      }
    };
  }, [currentUser?.id]);

  const resyncUnreadNotifications = useCallback(() => {
    void refreshUnreadFromDatabase();
  }, [refreshUnreadFromDatabase]);

  const markChatAsRead = (chatId: string) => {
    setChatNotifications((prev) => {
      const newMap = new Map(prev);
      const count = newMap.get(chatId) || 0;

      if (count > 0) {
        newMap.delete(chatId);

        setUnreadCount((prevCount) => Math.max(0, prevCount - count));
      }

      return newMap;
    });

    if (!socketRef.current) {
      if (currentUser?.id) {
        console.warn(
          "Socket no disponible al marcar chat como leído, el estado local se actualizó"
        );
      }
      return;
    }

    if (socketRef.current.connected) {
      socketRef.current.emit("markChatAsRead", chatId);
    } else {
      console.warn(
        "Socket no conectado, el estado local se actualizó pero no se sincronizó con el servidor"
      );
    }
  };

  const toggleNotificationSound = () => {
    setPlayNotificationSound((prev) => !prev);
  };

  const contextValue = {
    unreadCount,
    chatNotifications,
    markChatAsRead,
    resyncUnreadNotifications,
    playNotificationSound,
    toggleNotificationSound,
    connectionStatus,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
